import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  listFeedback, 
  verifyDevPassword, 
  listErrors,
  getUsageStats,
  getUsersOverview,
  listUploads,
  clearLegacyUploads,
  deleteUpload,
  listDatabases,
  selectDatabase,
  getDatabaseStats,
  getTableSchema,
  getTableData,
  executeQuery,
  exportDatabase,
  updateRows,
  deleteRows,
  clearTable,
  getOrganizationsStats,
  type FeedbackListParams,
  type ErrorLogListParams,
  type ClearLegacyParams,
  type DeleteUploadParams,
  type QueryRequest,
  type UpdateRequest,
  type DeleteRequest,
} from "../services/dev";

export const useListFeedback = (params: FeedbackListParams, enabled = true) => {
  return useQuery({
    queryKey: ["dev-feedback", params],
    queryFn: () => listFeedback(params),
    enabled: enabled && !!params.password,
    retry: false,
  });
};

export const useListErrors = (params: ErrorLogListParams, enabled = true) => {
  return useQuery({
    queryKey: ["dev-errors", params],
    queryFn: () => listErrors(params),
    enabled: enabled && !!params.password,
    retry: false,
  });
};

export const useUsageStats = (password: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-usage", password],
    queryFn: () => getUsageStats(password),
    enabled: enabled && !!password,
    retry: false,
  });
};

export const useUsersOverview = (password: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-users-overview", password],
    queryFn: () => getUsersOverview(password),
    enabled: enabled && !!password,
    retry: false,
  });
};

export const useOrganizationsStats = (password: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-organizations", password],
    queryFn: () => getOrganizationsStats(password),
    enabled: enabled && !!password,
    retry: false,
  });
};

export const useVerifyDevPassword = () => {
  return useMutation({
    mutationFn: verifyDevPassword,
  });
};

export const useListUploads = (password: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-uploads", password],
    queryFn: () => listUploads(password),
    enabled: enabled && !!password,
    retry: false,
  });
};

export const useClearLegacyUploads = () => {
  return useMutation({
    mutationFn: clearLegacyUploads,
  });
};

export const useDeleteUpload = () => {
  return useMutation({
    mutationFn: deleteUpload,
  });
};

export const useListDatabases = (password: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-database-list", password],
    queryFn: () => listDatabases(password),
    enabled: enabled && !!password,
    retry: false,
  });
};

export const useSelectDatabase = () => {
  return useMutation({
    mutationFn: ({ password, databasePath }: { password: string; databasePath: string }) =>
      selectDatabase(password, databasePath),
  });
};

export const useDatabaseStats = (password: string, databasePath?: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-database-stats", password, databasePath],
    queryFn: () => getDatabaseStats(password, databasePath),
    enabled: enabled && !!password,
    retry: false,
  });
};

export const useTableSchema = (password: string, tableName: string, databasePath?: string, enabled = true) => {
  return useQuery({
    queryKey: ["dev-table-schema", password, tableName, databasePath],
    queryFn: () => getTableSchema(password, tableName, databasePath),
    enabled: enabled && !!password && !!tableName,
    retry: false,
  });
};

export const useTableData = (
  password: string,
  tableName: string,
  page: number = 1,
  pageSize: number = 50,
  databasePath?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ["dev-table-data", password, tableName, page, pageSize, databasePath],
    queryFn: () => getTableData(password, tableName, page, pageSize, databasePath),
    enabled: enabled && !!password && !!tableName,
    retry: false,
  });
};

export const useExecuteQuery = () => {
  return useMutation({
    mutationFn: ({ password, request, databasePath }: { password: string; request: QueryRequest; databasePath?: string }) =>
      executeQuery(password, request, databasePath),
  });
};

export const useUpdateRows = () => {
  return useMutation({
    mutationFn: ({ password, request, databasePath }: { password: string; request: UpdateRequest; databasePath?: string }) =>
      updateRows(password, request, databasePath),
  });
};

export const useDeleteRows = () => {
  return useMutation({
    mutationFn: ({ password, request, databasePath }: { password: string; request: DeleteRequest; databasePath?: string }) =>
      deleteRows(password, request, databasePath),
  });
};

export const useExportDatabase = () => {
  return useMutation({
    mutationFn: ({ password, format }: { password: string; format?: "json" | "csv" }) =>
      exportDatabase(password, format),
  });
};

export const useClearTable = () => {
  return useMutation({
    mutationFn: ({ password, tableName, databasePath }: { password: string; tableName: string; databasePath?: string }) =>
      clearTable(password, tableName, databasePath),
  });
};